import { format, parse } from 'date-fns';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
} from 'expo-router';
import PaymentScheduleTimeline from 'features/payOverTime/components/PaymentScheduleTimeline/PaymentScheduleTimeline';
import usePayOverTimeLoanDetails from 'features/payOverTime/screens/LoanDetails/hooks/usePayOverTimeLoanDetails';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { formatCurrency } from 'shared/utils/string/currency';

const PaymentScheduleScreen = () => {
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'paymentSchedule',
  });

  const { loanId } = useLocalSearchParams<{
    loanId: string;
  }>();

  useViewDidFocusScreenAnalyticsEvent({
    key: '/pay-over-time/payment-schedule',
  });

  const {
    resultsObj: { paymentSchedule },
    error,
    fetching,
  } = usePayOverTimeLoanDetails(loanId, 'cache-and-network');

  const { bottom } = useSafeAreaInsets();
  const dismissToTab = useDismissToRootTab();

  const scheduleItems =
    paymentSchedule?.installments.map((installment) => {
      const abbreviatedDueDate = format(
        parse(installment.dueDateFormatted, 'MMMM d, yyyy', new Date()),
        'MMM d',
      );

      return {
        id: `${installment.dueDateFormatted}-${installment.amountDue.amount}`,
        label: `${formatCurrency(installment.amountDue.amount)} ${translatedText(
          'due',
        )} ${abbreviatedDueDate}`,
      };
    }) ?? [];

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('header'),
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<FullScreenLoading />}
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('error.title')}
              messageBody={translatedText('error.body')}
              primaryButton={{
                title: translatedText('error.button'),
                onPress: () => dismissToTab(Tabs.HOME),
              }}
            />
          ) : null
        }
      />
      <ScrollView>
        <PaymentScheduleTimeline
          items={scheduleItems}
          disclosureText={
            paymentSchedule ? translatedText('lateFeeCaption') : undefined
          }
        />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default PaymentScheduleScreen;
